/**
 * Data layer for `click-injection-detection-mobile`. Thin typed fetch wrappers
 * over /api/v1/click-injection-detection-mobile.
 *
 * Raw `fetch()` is confined to this file per the dash contract.
 */

export type InjectionVerdict = "injected" | "clean";

export interface ClickInjectionDetectionMobileEvent {
	id: string;
	tenant_id: string;
	event_id: string;
	device_id: string;
	click_ts: number;
	install_ts: number;
	tti_seconds: number;
	was_already_installed: number;
	install_referrer_ts: number | null;
	verdict: InjectionVerdict;
	reason: string | null;
	payload_json: string;
	created_at: number;
}

export interface ListClickInjectionDetectionMobileResponse {
	items: ClickInjectionDetectionMobileEvent[];
}

export interface CreateClickInjectionDetectionMobileBody {
	event_id: string;
	device_id: string;
	click_ts: number;
	install_ts: number;
	was_already_installed?: boolean;
	install_referrer_ts?: number | null;
}

export interface CreateClickInjectionDetectionMobileResponse {
	ok: boolean;
	event_id: string;
	verdict: InjectionVerdict;
	reason: string | null;
	tti_seconds: number;
}

const BASE = "/api/v1/click-injection-detection-mobile";

async function parseOrThrow<T>(res: Response): Promise<T> {
	const text = await res.text();
	let data: unknown = null;
	if (text) {
		try {
			data = JSON.parse(text);
		} catch {
			data = text;
		}
	}
	if (!res.ok) {
		const msg =
			data &&
			typeof data === "object" &&
			data !== null &&
			"error" in data &&
			typeof (data as { error: unknown }).error === "string"
				? (data as { error: string }).error
				: `HTTP ${res.status}`;
		throw new Error(msg);
	}
	return data as T;
}

export async function list(): Promise<ListClickInjectionDetectionMobileResponse> {
	const res = await fetch(BASE, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ListClickInjectionDetectionMobileResponse>(res);
}

export async function create(
	body: CreateClickInjectionDetectionMobileBody,
): Promise<CreateClickInjectionDetectionMobileResponse> {
	const res = await fetch(BASE, {
		method: "POST",
		credentials: "include",
		headers: {
			accept: "application/json",
			"content-type": "application/json",
		},
		body: JSON.stringify(body),
	});
	return parseOrThrow<CreateClickInjectionDetectionMobileResponse>(res);
}

export async function getOne(id: string): Promise<ClickInjectionDetectionMobileEvent> {
	const res = await fetch(`${BASE}/${encodeURIComponent(id)}`, {
		method: "GET",
		credentials: "include",
		headers: { accept: "application/json" },
	});
	return parseOrThrow<ClickInjectionDetectionMobileEvent>(res);
}

export const clickInjectionDetectionMobileQueryKeys = {
	all: ["click-injection-detection-mobile"] as const,
	list: () => [...clickInjectionDetectionMobileQueryKeys.all] as const,
	detail: (id: string) => [...clickInjectionDetectionMobileQueryKeys.all, id] as const,
};

export function verdictCounts(
	events: ClickInjectionDetectionMobileEvent[],
): { total: number; injected: number; clean: number } {
	let injected = 0;
	let clean = 0;
	for (const e of events) {
		if (e.verdict === "injected") injected += 1;
		else clean += 1;
	}
	return { total: events.length, injected, clean };
}

export function reasonCounts(
	events: ClickInjectionDetectionMobileEvent[],
): Array<{ reason: string; count: number }> {
	const m = new Map<string, number>();
	for (const e of events) {
		if (e.verdict !== "injected") continue;
		const key = e.reason ?? "unknown";
		m.set(key, (m.get(key) ?? 0) + 1);
	}
	return Array.from(m.entries())
		.map(([reason, count]) => ({ reason, count }))
		.sort((a, b) => b.count - a.count);
}
